import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Star, Sword } from 'lucide-react';

const characters = [
  { id: 1, name: 'Mickey', franchise: 'Disney', color: 'from-red-600 to-yellow-600' },
  { id: 2, name: 'Iron Man', franchise: 'Marvel', color: 'from-yellow-600 to-orange-600' },
  { id: 3, name: 'Luke Skywalker', franchise: 'Star Wars', color: 'from-blue-600 to-cyan-600' },
  { id: 4, name: 'Woody', franchise: 'Pixar', color: 'from-yellow-700 to-amber-600' },
  { id: 5, name: 'Elsa', franchise: 'Disney', color: 'from-cyan-600 to-blue-600' },
  { id: 6, name: 'Captain America', franchise: 'Marvel', color: 'from-blue-600 to-red-600' },
];

const stages = [
  { id: 1, name: 'Magic Kingdom', franchise: 'Disney', image: '🏰' },
  { id: 2, name: 'Stark Tower', franchise: 'Marvel', image: '🏢' },
  { id: 3, name: 'Death Star', franchise: 'Star Wars', image: '⭐' },
  { id: 4, name: 'Toy Box', franchise: 'Pixar', image: '🎁' },
];

export default function Game() {
  const [selectedP1, setSelectedP1] = useState<number | null>(null);
  const [selectedP2, setSelectedP2] = useState<number | null>(null);
  const [selectedStage, setSelectedStage] = useState<number | null>(null);

  const canStartMatch = selectedP1 && selectedP2 && selectedStage && selectedP1 !== selectedP2;

  const player1 = characters.find(c => c.id === selectedP1);
  const player2 = characters.find(c => c.id === selectedP2);
  const stage = stages.find(s => s.id === selectedStage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            ⚔️ Disney Ultimate Fight
          </h1>
          <p className="text-slate-400 mt-2">Cross-franchise battle arena</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 space-y-12">
        {/* Character Selection */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Sword className="w-6 h-6 text-cyan-400" />
            Select Characters
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div>
              <p className="text-sm text-slate-400 mb-3">Player 1</p>
              <div className="space-y-2">
                {characters.map(char => (
                  <button
                    key={char.id}
                    onClick={() => setSelectedP1(char.id)}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      selectedP1 === char.id
                        ? 'border-cyan-400 bg-gradient-to-r ' + char.color
                        : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    <div className="font-semibold text-white">{char.name}</div>
                    <div className="text-xs text-slate-300">{char.franchise}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm text-slate-400 mb-3">Player 2</p>
              <div className="space-y-2">
                {characters.map(char => (
                  <button
                    key={char.id}
                    onClick={() => setSelectedP2(char.id)}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      selectedP2 === char.id
                        ? 'border-purple-400 bg-gradient-to-r ' + char.color
                        : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    <div className="font-semibold text-white">{char.name}</div>
                    <div className="text-xs text-slate-300">{char.franchise}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stage Selection */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-purple-400" />
            Choose Battle Stage
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stages.map(stg => (
              <Card
                key={stg.id}
                className={`p-4 cursor-pointer transition-all border-2 ${
                  selectedStage === stg.id
                    ? 'border-purple-400 bg-slate-800'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
                onClick={() => setSelectedStage(stg.id)}
              >
                <div className="text-4xl mb-3">{stg.image}</div>
                <h3 className="font-semibold text-white text-sm">{stg.name}</h3>
                <Badge variant="secondary" className="mt-2 text-xs">{stg.franchise}</Badge>
              </Card>
            ))}
          </div>
        </section>

        {/* Match Preview & Start */}
        {(selectedP1 || selectedP2 || selectedStage) && (
          <section className="bg-gradient-to-r from-slate-800 to-slate-800 border border-slate-700 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              Match Preview
            </h2>

            <div className="grid grid-cols-3 gap-8">
              {player1 ? (
                <div className="text-center">
                  <div className={`h-24 rounded-lg bg-gradient-to-b ${player1.color} mb-3 flex items-center justify-center text-4xl`}>
                    {player1.name[0]}
                  </div>
                  <h3 className="font-bold text-white">{player1.name}</h3>
                  <p className="text-sm text-slate-400">{player1.franchise}</p>
                </div>
              ) : (
                <div className="text-center text-slate-500">Select Player 1</div>
              )}

              <div className="flex flex-col items-center justify-center">
                <div className="text-5xl font-bold text-slate-600 mb-2">VS</div>
                {stage && (
                  <div className="text-sm text-slate-400">
                    {stage.name} {stage.image}
                  </div>
                )}
              </div>

              {player2 ? (
                <div className="text-center">
                  <div className={`h-24 rounded-lg bg-gradient-to-b ${player2.color} mb-3 flex items-center justify-center text-4xl`}>
                    {player2.name[0]}
                  </div>
                  <h3 className="font-bold text-white">{player2.name}</h3>
                  <p className="text-sm text-slate-400">{player2.franchise}</p>
                </div>
              ) : (
                <div className="text-center text-slate-500">Select Player 2</div>
              )}
            </div>

            <Button
              onClick={() => alert(`Starting match: ${player1?.name} vs ${player2?.name} on ${stage?.name}!`)}
              disabled={!canStartMatch}
              className="w-full mt-8 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-bold text-lg py-6 disabled:opacity-50"
            >
              ⚡ START MATCH
            </Button>
          </section>
        )}
      </main>
    </div>
  );
}
